import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "./PaperDetails.css"; // Import CSS for styling

const PaperDetails = () => {
  const { paperId } = useParams();
  const location = useLocation();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewers, setReviewers] = useState([]); // Store reviewers for assignment
  const [domains, setDomains] = useState([]); // Domains for filtering reviewers
  const [filteredReviewers, setFilteredReviewers] = useState([]); // Filtered reviewers
  const [selectedDomain, setSelectedDomain] = useState("");
  const [primaryReviewer, setPrimaryReviewer] = useState("");
  const [secondaryReviewer, setSecondaryReviewer] = useState("");
  const [tertiaryReviewer, setTertiaryReviewer] = useState("");
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [presentationType, setPresentationType] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [allPapers, setAllPapers] = useState([]);
  const navigate = useNavigate();

  // Get navigation state
  const fromPage = location.state?.fromPage || 1;
  const paperIndex = location.state?.paperIndex || 0;
  const totalPapers = location.state?.totalPapers || 0;

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-paper/${paperId}`
      )
      .then((response) => setPaper(response.data.data))
      .catch((error) => setError("Failed to fetch paper details."))
      .finally(() => setLoading(false));
  }, [paperId]);

  useEffect(() => {
    // Fetch all papers for navigation
    const fetchAllPapers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-papers`
        );
        if (response.status === 200) {
          setAllPapers(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching all papers:", error);
      }
    };

    fetchAllPapers();
  }, []);

  useEffect(() => {
    const fetchReviewers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/get-reviewers`
        );
        if (response.status === 200) setReviewers(response.data.data);
      } catch (error) {
        console.error("Error fetching reviewers:", error);
        // Don't set error state - allow page to display without reviewers
        setReviewers([]); // Set empty array if no reviewers
      }
    };

    const fetchDomains = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/get-domains`
        );
        setDomains(response.data);
      } catch (error) {
        console.error("Error fetching domains:", error);
        setDomains([]); // Set empty array if no domains
      }
    };

    fetchReviewers();
    fetchDomains();
  }, []);

  const handleAssignReviewer = () => setIsAssignModalOpen(true);

  const filterReviewersByDomain = (selectedDomain) => {
    const filtered = reviewers.filter(
      (reviewer) =>
        reviewer.domain1 === selectedDomain ||
        reviewer.domain2 === selectedDomain ||
        reviewer.domain3 === selectedDomain
    );
    setFilteredReviewers(filtered);
  };

  const handleDomainChange = (e) => {
    setSelectedDomain(e.target.value);
    filterReviewersByDomain(e.target.value);
  };

  const handleAssignReviewers = async () => {
    if (!primaryReviewer && !secondaryReviewer && !tertiaryReviewer) {
      Swal.fire("Error", "Please select at least one reviewer.", "error");
      return;
    }

    const assignedReviewers = [
      primaryReviewer,
      secondaryReviewer,
      tertiaryReviewer,
    ]
      .filter((reviewer) => reviewer)
      .map((reviewer) => {
        const [firstName, lastName, email] = reviewer.split("|");
        return { name: `${firstName} ${lastName}`, email };
      });

    setIsAssigning(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/assign-reviewers`,
        {
          paperId: paper._id,
          reviewers: assignedReviewers,
        }
      );

      Swal.fire("Success", "Reviewers assigned successfully.", "success");
      setIsAssignModalOpen(false);
      // Refresh paper data
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-paper/${paperId}`
      );
      setPaper(response.data.data);
    } catch (error) {
      console.error("Error assigning reviewers:", error);
      Swal.fire("Error", "Failed to assign reviewers.", "error");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleShowReviews = () => {
    if (paper && paper.assignedReviewer && paper.assignedReviewer.length > 0) {
      setIsReviewModalOpen(true);
    } else {
      Swal.fire("No reviews", "No reviewers have been assigned yet.", "info");
    }
  };

  const handleAccept = async () => {
    // Open modal to select presentation type
    setIsAcceptModalOpen(true);
  };

  const handleConfirmAccept = async () => {
    if (!presentationType) {
      Swal.fire("Error", "Please select a presentation type.", "error");
      return;
    }

    setIsAccepting(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/accept-paper`,
        { 
          paperId,
          presentationType,
          changeReason: changeReason.trim() || null
        }
      );
      Swal.fire("Accepted!", "The paper has been accepted.", "success");
      setIsAcceptModalOpen(false);
      
      // Refresh paper data to show updated status
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-paper/${paperId}`
      );
      setPaper(response.data.data);
    } catch (error) {
      console.error("Error accepting paper:", error);
      setError("Failed to accept the paper.");
      Swal.fire("Error", "Failed to accept the paper.", "error");
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/reject-paper`,
        { paperId }
      );
      Swal.fire("Rejected!", "The paper has been rejected.", "success");
      
      // Refresh paper data to show updated status
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-paper/${paperId}`
      );
      setPaper(response.data.data);
    } catch (error) {
      console.error("Error rejecting paper:", error);
      setError("Failed to reject the paper.");
      Swal.fire("Error", "Failed to reject the paper.", "error");
    } finally {
      setIsRejecting(false);
    }
  };

  const closeModal = () => {
    setIsAssignModalOpen(false);
    setIsReviewModalOpen(false);
    setIsAcceptModalOpen(false);
    setPrimaryReviewer("");
    setSecondaryReviewer("");
    setTertiaryReviewer("");
    setPresentationType("");
    setChangeReason("");
  };

  const handleBackClick = () => {
    // Navigate back to the list with the page number preserved
    navigate("/admin/customize-paper", { 
      state: { returnToPage: fromPage } 
    });
  };

  const handlePreviousPaper = () => {
    if (allPapers.length === 0) return;
    
    const currentIndex = allPapers.findIndex(p => p._id === paperId);
    if (currentIndex === -1 || currentIndex <= 0) {
      Swal.fire({
        icon: "info",
        title: "No Previous Paper",
        text: "This is the first paper.",
        confirmButtonColor: "#5e71f6",
      });
      return;
    }

    const previousPaper = allPapers[currentIndex - 1];
    const previousPaperIndex = currentIndex - 1;
    
    // Calculate which page the previous paper belongs to (12 papers per page in UpdatePaper.js)
    const papersPerPage = 12;
    const previousPaperPage = Math.ceil((previousPaperIndex + 1) / papersPerPage);
    
    navigate(`/admin/paper-details/${previousPaper._id}`, {
      state: {
        fromPage: previousPaperPage,
        paperIndex: previousPaperIndex,
        totalPapers: allPapers.length
      },
      replace: true
    });
  };

  const handleNextPaper = () => {
    if (allPapers.length === 0) return;
    
    const currentIndex = allPapers.findIndex(p => p._id === paperId);
    if (currentIndex === -1 || currentIndex >= allPapers.length - 1) {
      Swal.fire({
        icon: "info",
        title: "No Next Paper",
        text: "This is the last paper.",
        confirmButtonColor: "#5e71f6",
      });
      return;
    }

    const nextPaper = allPapers[currentIndex + 1];
    const nextPaperIndex = currentIndex + 1;
    
    // Calculate which page the next paper belongs to (12 papers per page in UpdatePaper.js)
    const papersPerPage = 12;
    const nextPaperPage = Math.ceil((nextPaperIndex + 1) / papersPerPage);
    
    navigate(`/admin/paper-details/${nextPaper._id}`, {
      state: {
        fromPage: nextPaperPage,
        paperIndex: nextPaperIndex,
        totalPapers: allPapers.length
      },
      replace: true
    });
  };

  if (loading) {
    return (
      <div className="paper-details-wrapper">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading paper details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="paper-details-wrapper">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={() => navigate(-1)} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Format the submission date
  const formattedSubmissionTime = new Date(paper.createdAt).toLocaleString();

  // Apply classes to elements
  return (
    <div className="paper-details-wrapper">
      <div className="paper-details-container">
        {/* Header Section */}
        <div className="paper-header-section">
          <button onClick={handleBackClick} className="back-nav-button">
            <svg
              className="back-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
          <span className="paper-id-badge">Paper ID: {paper.paperID}</span>
          <span className={`status-badge status-${paper.status?.toLowerCase()}`}>
            {paper.status || 'Pending'}
          </span>
          <div className="paper-navigation-buttons">
            <button 
              onClick={handlePreviousPaper} 
              className="prev-paper-button"
              disabled={allPapers.length === 0}
            >
              <svg
                className="nav-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </button>
            <button 
              onClick={handleNextPaper} 
              className="next-paper-button"
              disabled={allPapers.length === 0}
            >
              Next
              <svg
                className="nav-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        <h1 className="paper-main-title">{paper.title}</h1>

        {/* Main Content Grid */}
        <div className="content-grid">
          {/* Abstract Section */}
          <div className="info-card full-width">
            <h3 className="info-card-title">Abstract</h3>
            <p className="info-card-text">{paper.abstract}</p>
          </div>

          {/* Keywords Section */}
          <div className="info-card">
            <h3 className="info-card-title">Keywords</h3>
            <div className="keywords-container">
              {(Array.isArray(paper.keywords)
                ? paper.keywords
                : paper.keywords?.split(',') || []
              ).map((keyword, index) => (
                <span key={index} className="keyword-tag">
                  {keyword.trim()}
                </span>
              ))}
            </div>
          </div>

          {/* Domain Section */}
          <div className="info-card">
            <h3 className="info-card-title">Domain Information</h3>
            {(() => {
              let domains = [];
              
              // Handle different formats of paperDomains
              if (Array.isArray(paper.paperDomains)) {
                // Check if it's an array of strings directly
                if (typeof paper.paperDomains[0] === 'string' && !paper.paperDomains[0].startsWith('[')) {
                  domains = paper.paperDomains;
                } else {
                  // Try to parse if first element is a JSON string
                  try {
                    domains = JSON.parse(paper.paperDomains[0]);
                  } catch {
                    domains = paper.paperDomains;
                  }
                }
              } else if (typeof paper.paperDomains === 'string') {
                try {
                  domains = JSON.parse(paper.paperDomains);
                } catch {
                  domains = [paper.paperDomains];
                }
              }

              return (
                <div className="domain-list">
                  <div className="domain-item">
                    <span className="domain-label">Main Domain:</span>
                    <span className="domain-value">{domains[0] || "N/A"}</span>
                  </div>
                  <div className="domain-item">
                    <span className="domain-label">Sub Domain 1:</span>
                    <span className="domain-value">{domains[1] || "N/A"}</span>
                  </div>
                  <div className="domain-item">
                    <span className="domain-label">Sub Domain 2:</span>
                    <span className="domain-value">{domains[2] || "N/A"}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Submission Details */}
          <div className="info-card">
            <h3 className="info-card-title">Submission Details</h3>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Submission Time:</span>
                <span className="detail-value">{formattedSubmissionTime}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Presentation Type:</span>
                <span className="detail-value">{paper.presentationType}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Paper Link:</span>
                <a
                  href={paper.cloudinaryURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="paper-link"
                >
                  <svg
                    className="link-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  View Paper
                </a>
              </div>
            </div>
          </div>

          {/* Authors Section */}
          <div className="info-card">
            <h3 className="info-card-title">Authors</h3>
            <div className="authors-grid">
              {paper.author && paper.author.length > 0 ? (
                paper.author.map((author) => (
                  <div key={author._id} className="author-card">
                    <div className="author-icon">
                      {author.firstName?.[0]}{author.lastName?.[0]}
                    </div>
                    <div className="author-info">
                      <p className="author-name">
                        {author.firstName} {author.lastName}
                      </p>
                      <p className="author-email">{author.email}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-data">No authors available</p>
              )}
            </div>
          </div>

          {/* Reviewers Section */}
          <div className="info-card full-width">
            <h3 className="info-card-title">Assigned Reviewers</h3>
            {paper.assignedReviewer && paper.assignedReviewer.length > 0 ? (
              <div className="reviewers-grid">
                {paper.assignedReviewer.map((reviewer, index) => (
                  <div key={index} className="reviewer-card">
                    <div className="reviewer-icon">
                      {reviewer.name?.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="reviewer-info">
                      <p className="reviewer-name">{reviewer.name}</p>
                      <p className="reviewer-email">{reviewer.email}</p>
                      {reviewer.reviewInfo && (
                        <span className="review-status-badge completed">
                          Review Submitted
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No reviewers assigned yet</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-section">
          <button className="action-btn assign-btn" onClick={handleAssignReviewer} disabled={isAssigning}>
            {isAssigning ? (
              <>
                <svg className="btn-icon spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Assign Reviewers
              </>
            )}
          </button>
          <button className="action-btn review-btn" onClick={handleShowReviews}>
            <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Show Reviews
          </button>
          <button
            className="action-btn accept-btn"
            onClick={handleAccept}
            disabled={paper.status === "Accepted" || paper.status === "Rejected" || isAccepting}
          >
            {isAccepting ? (
              <>
                <svg className="btn-icon spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Accepting...
              </>
            ) : (
              <>
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {paper.status === "Accepted" ? "Accepted" : "Accept Paper"}
              </>
            )}
          </button>
          <button
            className="action-btn reject-btn"
            onClick={handleReject}
            disabled={paper.status === "Accepted" || paper.status === "Rejected" || isRejecting}
          >
            {isRejecting ? (
              <>
                <svg className="btn-icon spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Rejecting...
              </>
            ) : (
              <>
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {paper.status === "Rejected" ? "Rejected" : "Reject Paper"}
              </>
            )}
          </button>
        </div>

        {/* Assign Reviewers Modal */}
        {isAssignModalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">Assign Reviewers</h2>
            <label htmlFor="domain" className="modal-label">
              Select Domain:
            </label>
            <select
              id="domain"
              onChange={handleDomainChange}
              value={selectedDomain}
              className="modal-select"
            >
              <option value="">Select a Domain</option>
              {domains.map((domain) => (
                <option key={domain._id} value={domain.mainDomain}>
                  {domain.mainDomain}
                </option>
              ))}
            </select>
            {filteredReviewers.length === 0 && selectedDomain && (
              <p>No reviewers found for this domain.</p>
            )}
            {filteredReviewers.length > 0 && (
              <>
                <select
                  value={primaryReviewer}
                  onChange={(e) => setPrimaryReviewer(e.target.value)}
                  className="modal-select"
                >
                  <option value="">Select Reviewer 1</option>
                  {filteredReviewers.map((reviewer) => (
                    <option
                      key={reviewer.email}
                      value={`${reviewer.firstName}|${reviewer.lastName}|${reviewer.email}`}
                    >
                      {reviewer.firstName} {reviewer.lastName} ({reviewer.email}
                      )
                    </option>
                  ))}
                </select>
                <select
                  value={secondaryReviewer}
                  onChange={(e) => setSecondaryReviewer(e.target.value)}
                  className="modal-select"
                >
                  <option value="">Select Reviewer 2</option>
                  {filteredReviewers.map((reviewer) => (
                    <option
                      key={reviewer.email}
                      value={`${reviewer.firstName}|${reviewer.lastName}|${reviewer.email}`}
                    >
                      {reviewer.firstName} {reviewer.lastName} ({reviewer.email}
                      )
                    </option>
                  ))}
                </select>
                <select
                  value={tertiaryReviewer}
                  onChange={(e) => setTertiaryReviewer(e.target.value)}
                  className="modal-select"
                >
                  <option value="">Select Reviewer 3</option>
                  {filteredReviewers.map((reviewer) => (
                    <option
                      key={reviewer.email}
                      value={`${reviewer.firstName}|${reviewer.lastName}|${reviewer.email}`}
                    >
                      {reviewer.firstName} {reviewer.lastName} ({reviewer.email}
                      )
                    </option>
                  ))}
                </select>
              </>
            )}
            <button
              onClick={handleAssignReviewers}
              className="modal-button-assign"
              disabled={isAssigning}
            >
              {isAssigning ? (
                <>
                  <svg className="btn-icon spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px', marginRight: '8px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Assigning...
                </>
              ) : (
                'Assign'
              )}
            </button>
              <button onClick={closeModal} className="modal-button-close" disabled={isAssigning}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Show Reviews Modal */}
        {isReviewModalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="review-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
            <h2 className="modal-title">Review Details</h2>
            <div className="review-table-container">
              <table className="review-table">
                <thead>
                  <tr>
                    <th>Reviewer Name</th>
                    <th>Reviewer Email</th>
                    <th>Review Message</th>
                    <th>Review Image</th>
                    <th>Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {paper.assignedReviewer &&
                  paper.assignedReviewer.length > 0 ? (
                    paper.assignedReviewer.map((reviewer, index) => (
                      <tr key={index}>
                        <td>{reviewer.name}</td>
                        <td>{reviewer.email}</td>
                        <td>{reviewer.reviewInfo?.reviewMessage || "-"}</td>
                        <td>
                          {reviewer.reviewInfo?.reviewPicURL ? (
                            <a
                              href={reviewer.reviewInfo.reviewPicURL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Image
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{reviewer.reviewInfo?.recommendation || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">No reviews available</td>
                    </tr>
                  )}
                </tbody>
                </table>
            </div>
            <button onClick={closeModal} className="modal-button-close">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Accept Paper Modal */}
      {isAcceptModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Accept Paper</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '15px', color: '#64748b' }}>
                Paper was submitted as: <strong style={{ color: '#1e293b' }}>{paper.presentationType || 'Oral'}</strong>
              </p>
              
              <label htmlFor="presentationType" className="modal-label">
                Accept as Presentation Type: <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="presentationType"
                value={presentationType}
                onChange={(e) => setPresentationType(e.target.value)}
                className="modal-select"
                style={{ marginBottom: '20px' }}
              >
                <option value="">Select Presentation Type</option>
                <option value="Oral">Oral Presentation</option>
                <option value="Poster">Poster Presentation</option>
              </select>

              {presentationType && presentationType !== paper.presentationType && (
                <div style={{ marginTop: '20px' }}>
                  <label htmlFor="changeReason" className="modal-label">
                    Reason for changing presentation type (Optional):
                  </label>
                  <textarea
                    id="changeReason"
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    className="modal-select"
                    rows="4"
                    placeholder="Explain why the presentation type is being changed..."
                    style={{ 
                      resize: 'vertical',
                      minHeight: '80px',
                      fontFamily: 'inherit',
                      padding: '10px'
                    }}
                  />
                  <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                    This reason will be included in the acceptance email to the author.
                  </p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
              <button
                onClick={handleConfirmAccept}
                className="modal-button-assign"
                disabled={isAccepting || !presentationType}
                style={{ flex: 1 }}
              >
                {isAccepting ? (
                  <>
                    <svg className="btn-icon spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px', marginRight: '8px' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Accepting...
                  </>
                ) : (
                  'Confirm Accept'
                )}
              </button>
              <button 
                onClick={closeModal} 
                className="modal-button-close" 
                disabled={isAccepting}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PaperDetails;
